/* eslint-disable no-console */
import { v4 as uuid } from 'uuid'
import { captureException } from '@sentry/nextjs'
import { differenceInWeeks } from 'date-fns'

import ROUTES from '~/shared/constants/routes.const'
import { callApi } from '~/services/axiosInstance.service'
import { Event, EventData, VideoWatchEvent } from '~/behavior-tracking/types'
import { getViewportId } from '~/behavior-tracking/utility/viewport-id.utility'
import viewEventsStore from '~/behavior-tracking/utility/view-events-store'
import { getPagePathOffset } from '~/behavior-tracking/utility/browser.utility'
import { getEventKey } from '~/behavior-tracking/utility/helpers'
import videoEventsStore from '~/behavior-tracking/utility/video-events-store'
import { checkViewKeyInBufferWithProduct } from '~/behavior-tracking/utility/checkViewKeyInBufferWithProduct'

import { MAX_PATH_LENGTH } from '../constants/tracking-constants'

const EVENT_SCHEMA_VERSION = '1'
const MAX_REMOVAL_PER_SESSION = 50
const EVENT_VALIDITY_WEEKS = 2
const MAX_SEND_BATCH_SIZE = 50

async function submitEvents(events: Event[], videoEvents: VideoWatchEvent[]): Promise<void> {
  const response = await callApi({
    url: '/v1/user-stats/events',
    method: 'POST',
    data: { events, videoEvents },
  })

  if (response && 'error' in response) {
    const statusCode = response.meta?.response?.status ?? 'unknown'

    throw new Error(`Events submission failed with status code: ${statusCode}`)
  }
}

export async function logUserActivity(userId: number, eventData: EventData): Promise<void> {
  try {
    await createPageViewEvent(userId, eventData)
  } catch (err) {
    captureException(err, { tags: { feature: 'view-tracking', action: 'create-event' } })
    console.error('Failed to create activity log', err)
  }
}

async function createPageViewEvent(userId: number, eventData: EventData): Promise<void> {
  const pagePathOffset = getPagePathOffset()

  const event = {
    ...eventData,
    viewKey: eventData.viewKey.slice(0, MAX_PATH_LENGTH),
    extra: {
      ...(eventData.extra ?? {}),
      url: typeof document !== 'undefined' && document.URL.slice(pagePathOffset),
      pageVisible: typeof document !== 'undefined' && document.visibilityState === 'visible',
      schemaVersion: EVENT_SCHEMA_VERSION,
    },
    id: uuid(),
    time: new Date().toISOString(),
    viewportId: getViewportId(),
  }

  await viewEventsStore.setItem(getEventKey(userId, event.id), event)
}

export async function flushEvents(userId: number): Promise<
  | {
      eventsRelatedToVCC: boolean
      eventsRelatedToCurriculum: boolean
      eventsRelatedToVCCVideo: boolean
    }
  | undefined
> {
  const scopePrefix = `${userId}.`
  const eventsBuffer: Event[] = []
  const videoEventsBuffer: VideoWatchEvent[] = []

  let batchSize = 0
  await viewEventsStore.iterate((value: Event, key) => {
    if (key.startsWith(scopePrefix)) {
      eventsBuffer.push(value)
      batchSize += 1
    }

    if (batchSize >= MAX_SEND_BATCH_SIZE) {
      return 'skipped-due-to-max-send-batch-size-reached'
    }
  })

  let videoEventsBufferSize = 0
  await videoEventsStore.iterate((value: VideoWatchEvent, key) => {
    if (key.startsWith(scopePrefix)) {
      videoEventsBuffer.push(value)
      videoEventsBufferSize += 1
    }

    if (videoEventsBufferSize >= MAX_SEND_BATCH_SIZE) {
      return 'skipped-due-to-max-send-batch-size-reached'
    }
  })

  if (videoEventsBuffer.length < 1 && eventsBuffer.length < 1) {
    return
  }

  await submitEvents(eventsBuffer, videoEventsBuffer)

  const allEvents = [...eventsBuffer, ...videoEventsBuffer]

  const eventsRelatedToVCCVideo = checkViewKeyInBufferWithProduct(videoEventsBuffer, ROUTES.BOOTCAMP)
  const eventsRelatedToVCC = checkViewKeyInBufferWithProduct(allEvents, ROUTES.BOOTCAMP)
  const eventsRelatedToCurriculum = checkViewKeyInBufferWithProduct(allEvents, ROUTES.CURRICULUM)

  eventsBuffer.forEach((event) => {
    viewEventsStore.removeItem(getEventKey(userId, event.id))
  })

  videoEventsBuffer.forEach((event) => {
    videoEventsStore.removeItem(getEventKey(userId, event.id))
  })

  const returnObj = {
    eventsRelatedToVCC: false,
    eventsRelatedToCurriculum: false,
    eventsRelatedToVCCVideo: false,
  }

  if (eventsRelatedToVCC) {
    returnObj.eventsRelatedToVCC = true
  }

  if (eventsRelatedToCurriculum) {
    returnObj.eventsRelatedToCurriculum = true
  }

  if (eventsRelatedToVCCVideo) {
    returnObj.eventsRelatedToVCCVideo = true
  }

  return returnObj
}

export async function removedExpiredEvents(): Promise<void> {
  const now = new Date()
  let expiredEventsCount = 0

  await viewEventsStore.iterate((value: Event, key) => {
    const eventTime = value?.time ? new Date(value.time) : now
    const isEventExpired = differenceInWeeks(now, eventTime) >= EVENT_VALIDITY_WEEKS

    if (isEventExpired) {
      viewEventsStore.removeItem(key)
      expiredEventsCount += 1
    }

    if (expiredEventsCount >= MAX_REMOVAL_PER_SESSION) {
      return 'skipped-due-to-max-removal-received'
    }
  })

  await videoEventsStore.iterate((value: Event, key) => {
    const eventTime = value?.time ? new Date(value.time) : now
    const isEventExpired = differenceInWeeks(now, eventTime) >= EVENT_VALIDITY_WEEKS

    if (isEventExpired) {
      viewEventsStore.removeItem(key)
      expiredEventsCount += 1
    }

    if (expiredEventsCount >= MAX_REMOVAL_PER_SESSION) {
      return 'skipped-due-to-max-removal-received'
    }
  })
}
